/**
 * OverviewTab — Summary cards, activity trends, and live infrastructure panel.
 * - Trends: fetched once per activation, with a days selector.
 * - Live panel: polled every 5 s via setInterval + cleanup on unmount/tab-switch.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  getAdminTrends,
  getAdminLive,
  getAdminUserActivity,
  type ActivityTrendsResult,
  type AdminLiveSnapshot,
  type UserActivityRow,
} from '../../api/client.js';
import { TrendChart } from './TrendChart.js';

// ============================================================================
// Helpers
// ============================================================================

function formatBytes(bytes: number): string {
  if (bytes >= 1_073_741_824) return `${(bytes / 1_073_741_824).toFixed(2)} GB`;
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

function formatDuration(s: number): string {
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}

function formatTimeAgo(iso: string | null): string {
  if (!iso) return 'never';
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return `${Math.floor(diffHr / 24)}d ago`;
}

// ============================================================================
// Summary Card
// ============================================================================

interface SummaryCardProps {
  label: string;
  value: string;
  sub?: string;
}

function SummaryCard({ label, value, sub }: SummaryCardProps) {
  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
      <div className="text-2xl font-bold text-white tabular-nums">{value}</div>
      <div className="text-sm text-gray-400 mt-1">{label}</div>
      {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

// ============================================================================
// Live Panel
// ============================================================================

interface LivePanelProps {
  snap: AdminLiveSnapshot | null;
  lastUpdatedAt: Date | null;
}

function LivePanel({ snap, lastUpdatedAt }: LivePanelProps) {
  if (!snap) {
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 text-gray-500 text-sm">
        Waiting for first live snapshot...
      </div>
    );
  }

  const p = snap.process;
  const db = snap.dbPool;
  const q = snap.queue;
  const rc = snap.requestCounters;
  const cron = snap.cron;

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-white">Live Snapshot</h3>
        <span className="text-xs text-gray-500">
          {lastUpdatedAt ? `Updated ${formatTimeAgo(lastUpdatedAt.toISOString())}` : ''}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 text-xs">
        {/* Process */}
        <div className="bg-gray-800 rounded-lg p-3 space-y-1.5">
          <div className="text-gray-400 font-medium uppercase tracking-wide text-xs">Process</div>
          <div className="flex justify-between"><span className="text-gray-500">Uptime</span><span className="text-gray-200">{formatDuration(p.uptimeS)}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">CPU</span><span className="text-gray-200">{p.cpuPercent.toFixed(1)}%</span></div>
          <div className="flex justify-between"><span className="text-gray-500">RSS</span><span className="text-gray-200">{formatBytes(p.memoryRss)}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Heap used</span><span className="text-gray-200">{formatBytes(p.memoryHeapUsed)}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Heap total</span><span className="text-gray-200">{formatBytes(p.memoryHeapTotal)}</span></div>
        </div>

        {/* Event Loop */}
        <div className="bg-gray-800 rounded-lg p-3 space-y-1.5">
          <div className="text-gray-400 font-medium uppercase tracking-wide text-xs">Event Loop</div>
          <div className="flex justify-between"><span className="text-gray-500">Lag mean</span><span className={`${p.eventLoopLagMeanMs > 50 ? 'text-yellow-400' : 'text-gray-200'}`}>{p.eventLoopLagMeanMs.toFixed(1)} ms</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Lag p99</span><span className={`${p.eventLoopLagP99Ms > 200 ? 'text-red-400' : 'text-gray-200'}`}>{p.eventLoopLagP99Ms.toFixed(1)} ms</span></div>
        </div>

        {/* DB Pool */}
        <div className="bg-gray-800 rounded-lg p-3 space-y-1.5">
          <div className="text-gray-400 font-medium uppercase tracking-wide text-xs">DB Pool</div>
          <div className="flex justify-between"><span className="text-gray-500">Total</span><span className="text-gray-200">{db.totalCount} / {db.max}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Idle</span><span className="text-gray-200">{db.idleCount}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Waiting</span><span className={`${db.waitingCount > 0 ? 'text-yellow-400' : 'text-gray-200'}`}>{db.waitingCount}</span></div>
        </div>

        {/* Queue */}
        <div className="bg-gray-800 rounded-lg p-3 space-y-1.5">
          <div className="text-gray-400 font-medium uppercase tracking-wide text-xs">Job Queue</div>
          <div className="flex justify-between"><span className="text-gray-500">Queued</span><span className="text-gray-200">{q.queued}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Running</span><span className="text-gray-200">{q.running}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Workers</span><span className="text-gray-200">{q.activeWorkers}</span></div>
        </div>

        {/* Request counters */}
        <div className="bg-gray-800 rounded-lg p-3 space-y-1.5">
          <div className="text-gray-400 font-medium uppercase tracking-wide text-xs">Requests</div>
          <div className="flex justify-between"><span className="text-gray-500">Total</span><span className="text-gray-200">{rc.totalRequests.toLocaleString()}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">4xx</span><span className={`${rc.total4xx > 0 ? 'text-yellow-400' : 'text-gray-200'}`}>{rc.total4xx.toLocaleString()}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">5xx</span><span className={`${rc.total5xx > 0 ? 'text-red-400' : 'text-gray-200'}`}>{rc.total5xx.toLocaleString()}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Last 60s req</span><span className="text-gray-200">{rc.last60s.requests}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Last 60s err</span><span className={`${rc.last60s.errors > 0 ? 'text-red-400' : 'text-gray-200'}`}>{rc.last60s.errors}</span></div>
        </div>

        {/* Cron */}
        <div className="bg-gray-800 rounded-lg p-3 space-y-1.5">
          <div className="text-gray-400 font-medium uppercase tracking-wide text-xs">Cron</div>
          <div className="flex justify-between"><span className="text-gray-500">Incr refresh</span><span className="text-gray-200">/{cron.incrementalRefreshIntervalMin}m</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Consistency</span><span className="text-gray-200">/{cron.consistencyCheckIntervalMin}m</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Daily UTC hr</span><span className="text-gray-200">{String(cron.dailyFullRefreshUtcHour).padStart(2, '0')}:00</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Next daily</span><span className="text-gray-200">{formatTimeAgo(cron.nextDailyRefresh)}</span></div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main OverviewTab
// ============================================================================

const DAYS_OPTIONS = [7, 30, 90] as const;
type DaysOption = typeof DAYS_OPTIONS[number];

export function OverviewTab() {
  const [days, setDays] = useState<DaysOption>(30);
  const [trends, setTrends] = useState<ActivityTrendsResult | null>(null);
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [users, setUsers] = useState<UserActivityRow[]>([]);

  const [liveSnap, setLiveSnap] = useState<AdminLiveSnapshot | null>(null);
  const [liveUpdatedAt, setLiveUpdatedAt] = useState<Date | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch trends
  const fetchTrends = useCallback(async (d: DaysOption) => {
    setTrendsLoading(true);
    try {
      const result = await getAdminTrends(d);
      setTrends(result);
    } catch {
      // Non-critical
    } finally {
      setTrendsLoading(false);
    }
  }, []);

  // Fetch users for summary cards
  const fetchUsers = useCallback(async () => {
    try {
      const result = await getAdminUserActivity();
      setUsers(result.users);
    } catch {
      // Non-critical
    }
  }, []);

  // Poll live endpoint every 5 s
  const startLivePolling = useCallback(() => {
    const poll = async () => {
      try {
        const snap = await getAdminLive();
        setLiveSnap(snap);
        setLiveUpdatedAt(new Date());
      } catch {
        // Swallow transient errors — keep last-good snapshot
      }
    };

    // Immediate first fetch
    poll();
    pollRef.current = setInterval(() => { void poll(); }, 5000);
  }, []);

  const stopLivePolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => {
    void fetchUsers();
    startLivePolling();

    return () => {
      stopLivePolling();
    };
  }, [fetchUsers, startLivePolling, stopLivePolling]);

  // When days changes, refetch trends
  useEffect(() => {
    void fetchTrends(days);
  }, [days, fetchTrends]);

  // Summary card values
  const activeUsers = users.filter((u) => {
    if (!u.lastActiveAt) return false;
    return Date.now() - new Date(u.lastActiveAt).getTime() < 7 * 24 * 60 * 60 * 1000;
  }).length;
  const totalBacktests = users.reduce((sum, u) => sum + u.backtestCount, 0);
  const jobsRunning = liveSnap?.queue.running ?? 0;
  const successRate = liveSnap
    ? ((liveSnap.requestCounters.totalRequests - liveSnap.requestCounters.total5xx) /
        Math.max(liveSnap.requestCounters.totalRequests, 1)) *
      100
    : null;

  // Build chart series from trends
  const backtestSeries = trends
    ? trends.series.map((e) => ({ time: e.date, value: e.backtests }))
    : [];
  const gridSearchSeries = trends
    ? trends.series.map((e) => ({ time: e.date, value: e.gridSearch }))
    : [];
  const wfSeries = trends
    ? trends.series.map((e) => ({ time: e.date, value: e.walkForward }))
    : [];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryCard
          label="Active Users (7d)"
          value={String(activeUsers)}
          sub={`of ${users.length} total`}
        />
        <SummaryCard
          label="Total Backtests"
          value={totalBacktests.toLocaleString()}
        />
        <SummaryCard
          label="Jobs Running"
          value={String(jobsRunning)}
          sub={liveSnap ? `${liveSnap.queue.queued} queued` : undefined}
        />
        <SummaryCard
          label="API Success Rate"
          value={successRate !== null ? `${successRate.toFixed(1)}%` : '—'}
          sub={liveSnap ? `${liveSnap.requestCounters.totalRequests.toLocaleString()} total req` : undefined}
        />
      </div>

      {/* Trends section */}
      <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-white">Activity Trends</h3>
          <div className="flex items-center gap-2">
            {DAYS_OPTIONS.map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                  days === d
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>

        {trendsLoading ? (
          <div className="flex items-center justify-center py-10 text-gray-500 text-sm gap-2">
            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Loading trends...
          </div>
        ) : (
          <>
            <div>
              <div className="text-xs text-gray-500 mb-1">Backtests per day</div>
              <TrendChart data={backtestSeries} color="#22C55E" height={140} />
            </div>
            <div>
              <div className="flex items-center gap-4 text-xs mb-1">
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-blue-400" />Grid Search</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-purple-400" />Walk-Forward</span>
              </div>
              <TrendChart
                series={[
                  { label: 'Grid Search', color: '#60A5FA', data: gridSearchSeries },
                  { label: 'Walk-Forward', color: '#A78BFA', data: wfSeries },
                ]}
                height={140}
              />
            </div>
          </>
        )}
      </div>

      {/* Live Panel */}
      <LivePanel snap={liveSnap} lastUpdatedAt={liveUpdatedAt} />
    </div>
  );
}
